import { useEffect, useRef, useState } from "react";
import { Check, ChevronDown, MapPin, Search } from "lucide-react";
import "@/styles/departurePicker.css";

export default function DeparturePicker({ id, label, origin, originName, metros, onChange }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const root = useRef(null);
  const trigger = useRef(null);
  const input = useRef(null);
  const options = metros.length ? metros : [{ code: origin, name: originName }];
  const matches = options.filter(metro => `${metro.name} ${metro.code} ${metro.country || ""}`.toLowerCase().includes(query.trim().toLowerCase()));
  const close = () => { setOpen(false); trigger.current?.focus(); };
  const choose = metro => { onChange(metro.code); close(); };
  useEffect(() => {
    if (!open) return undefined;
    input.current?.focus();
    const outside = event => { if (!root.current?.contains(event.target)) setOpen(false); };
    document.addEventListener("pointerdown", outside);
    return () => document.removeEventListener("pointerdown", outside);
  }, [open]);
  useEffect(() => {
    if (open) root.current?.querySelector('[data-active="true"]')?.scrollIntoView?.({ block: "nearest" });
  }, [active, open]);
  return <div className="departure-picker" ref={root} onBlur={event => {
    if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false);
  }}>
    <button id={id} ref={trigger} type="button" className="departure-picker-trigger" aria-label={`${label}: From ${originName} (${origin})`} aria-haspopup="listbox" aria-expanded={open} aria-controls={`${id}-list`} onClick={() => { setOpen(value => !value); setQuery(""); setActive(0); }}>
      <MapPin size={18} aria-hidden="true" /><span>From {originName} ({origin})</span><ChevronDown size={16} aria-hidden="true" />
    </button>
    {open && <div className="departure-picker-menu">
      <div className="departure-picker-search"><Search size={16} aria-hidden="true" /><input ref={input} role="combobox" aria-label={`Search ${label.toLowerCase()}`} aria-expanded="true" aria-controls={`${id}-list`} aria-autocomplete="list" aria-activedescendant={matches[active] ? `${id}-${matches[active].code}` : undefined} value={query} placeholder="Search city or airport code" onChange={event => { setQuery(event.target.value); setActive(0); }} onKeyDown={event => {
        if (event.key === "Escape") { event.preventDefault(); close(); }
        if (event.key === "ArrowDown" || event.key === "ArrowUp") {
          event.preventDefault(); setActive(value => matches.length ? (value + (event.key === "ArrowDown" ? 1 : -1) + matches.length) % matches.length : 0);
        }
        if (event.key === "Enter") { event.preventDefault(); if (matches[active]) choose(matches[active]); }
      }} /></div>
      <div id={`${id}-list`} role="listbox" aria-label={label} className="departure-picker-list">
        {matches.map((metro, index) => <button key={metro.code} id={`${id}-${metro.code}`} type="button" role="option" tabIndex={-1} aria-selected={origin === metro.code} data-active={active === index} onMouseDown={event => event.preventDefault()} onMouseEnter={() => setActive(index)} onClick={() => choose(metro)}><span>{metro.name} <small>{metro.code}</small></span>{origin === metro.code && <Check size={16} aria-hidden="true" />}</button>)}
        {!matches.length && <p role="status">No matching departure cities.</p>}
      </div>
    </div>}
  </div>;
}
